import clsx from 'clsx';
import React from 'react';
import { useDispatch } from 'react-redux';

import AnchorLink from '@/src/components/common/Elements/AnchorLink/AnchorLink';
import Button from '@/src/components/common/Elements/Button/Button';
import TileCard from '@/src/components/common/Elements/Card/TileCard/TileCard';
import Icon, { ICONS, ICON_TYPES } from '@/src/components/common/Elements/Icon/Icon';
import Link from '@/src/components/common/Elements/Link/Link';
import LongTextEllipsisInBetween from '@/src/components/common/Elements/LongText/LongTextEllipsisInBetween';
import Tooltip from '@/src/components/common/Elements/Tooltip/Tooltip';
import StatusIndicator from '@/src/components/project/Common/StatusIndicator/StatusIndicator';
import { TaskStatusEnum } from '@/src/components/task/Config';
import { SubtaskTypeEnum } from '@/src/components/task/Details/Tabs/Subtasks/Config';
import { getFilePathExtension } from '@/src/globalFunction';
import { initialize as initializeTaskPanel } from '@/src/reducers/tasks/taskForm/taskForm.actions';

import WorkstreamAvatar from '../../../../Elements/WorkstreamAvatar/WorkstreamAvatar';
import { SubtaskReport } from '../../types';

const getIcon = ({
  subtaskReport,
  taskCompleted,
}: {
  subtaskReport: SubtaskReport;
  taskCompleted: boolean;
}) => {
  const defaultIcon = ICONS.file;

  if (subtaskReport.type === SubtaskTypeEnum.DOCUMENT) {
    const ext = subtaskReport.rawName
      ? getFilePathExtension(subtaskReport.rawName).toLowerCase()
      : null;

    if (!ext) {
      return (
        <Icon
          icon={defaultIcon}
          className={clsx('tw-mr-[7px] tw-text-[32px] tw-text-cfo-grey-600')}
        />
      );
    }

    return (
      <Icon
        icon={ICONS[ext] || defaultIcon}
        className={clsx(
          'tw-mr-[7px] tw-text-[20px] tw-text-cfo-black',
          !taskCompleted && 'tw-opacity-[0.50]'
        )}
      />
    );
  }

  return (
    <Icon
      icon={ICONS.link}
      className={clsx(
        'tw-rotate-[-50deg] tw-mr-[7px] tw-text-[24px]',
        !subtaskReport.href && !taskCompleted && 'tw-text-cfo-grey-600',
        subtaskReport.href && !taskCompleted && 'tw-text-cfo-blue-600 tw-opacity-[0.50]',
        taskCompleted && 'tw-text-cfo-blue-600'
      )}
    />
  );
};

const getLabel = ({
  subtaskReport,
  taskCompleted,
}: {
  subtaskReport: SubtaskReport;
  taskCompleted: boolean;
}) => {
  const label = (
    <div className={clsx('tw-flex tw-items-center', 'tw-h-[35px]')}>
      {getIcon({ subtaskReport, taskCompleted })}

      <LongTextEllipsisInBetween
        value={subtaskReport.label}
        id={subtaskReport.id}
        maxLength={50}
        firstCharactersLength={25}
        lastCharactersLength={22}
      />
    </div>
  );

  if (!taskCompleted) {
    return label;
  }

  return subtaskReport.type === SubtaskTypeEnum.DOCUMENT ? (
    <Link to={subtaskReport.href} target="_blank">
      {label}
    </Link>
  ) : (
    <AnchorLink href={subtaskReport.href} target="_blank">
      {label}
    </AnchorLink>
  );
};

const getAdjustedDateColor = ({
  taskCompleted,
  isDelayed,
}: {
  taskCompleted: boolean;
  isDelayed: boolean;
}) => {
  if (isDelayed && !taskCompleted) {
    return 'tw-text-cfo-red-400';
  }

  return 'tw-text-cfo-black';
};

type SubtaskReportItemProps = {
  subtaskReport: SubtaskReport;
};

const SubtaskReportItem = ({ subtaskReport }: SubtaskReportItemProps) => {
  const dispatch = useDispatch();
  const taskCompleted = subtaskReport.status === TaskStatusEnum.Completed;
  const label = getLabel({ subtaskReport, taskCompleted });

  async function handleOpenTask() {
    dispatch({ type: 'SET_SCREEN_LOADER', Loading: true });

    await dispatch(initializeTaskPanel(subtaskReport.taskId));

    $(`#task-details`).modal('show');
    dispatch({ type: 'SET_SCREEN_LOADER', Loading: false });
  }

  return (
    <TileCard
      className={clsx(
        'tw-max-w-[250px] tw-min-w-[250px] tw-w-full',
        'tw-my-[2px] tw-mx-[5px]',
        'tw-border-cfo-grey-500 tw-shadow-none'
      )}
    >
      <div
        className={clsx(
          'tw-flex tw-items-center tw-justify-end',
          'tw-border-b tw-border-cfo-grey-500 tw-border-solid',
          'tw-pb-[10px] tw-px-[20px] tw-mx-[-20px] tw-mb-[20px]'
        )}
      >
        <WorkstreamAvatar
          id={subtaskReport.id}
          name={subtaskReport.workstreamName}
          avatar={'default.png'}
          workstreamColor={subtaskReport.workstreamColor}
        />

        <a
          className={clsx(
            'container--date_wrapper tw-flex tw-items-center',
            'tw-font-brandonTextMedium'
          )}
        >
          <span
            className={clsx(
              'tw-text-[10px]',
              getAdjustedDateColor({ taskCompleted, isDelayed: subtaskReport.isDelayed })
            )}
          >
            {subtaskReport.adjustedDate}
          </span>
          <Button
            data-tooltip-id={`sti_${subtaskReport.id}_tooltip`}
            onClick={handleOpenTask}
            className={clsx('tw-leading-none tw-mr-0')}
            tooltip={{
              content: 'Go to task'
            }}
          >
            <Icon icon={ICONS.arrow_right} className={clsx('tw-text-[18px] tw-text-cfo-black')} />
          </Button>
        </a>
      </div>
      <div
        className={clsx(
          'container--fileName_wrapper',
          'tw-flex tw-items-center',
          'tw-pb-[23px]',
          'tw-text-cfo-black',
          taskCompleted && 'hover:tw-text-cfo-blue-600'
        )}
      >
        {label}
      </div>
      <StatusIndicator
        status={subtaskReport.status}
        className={clsx(
          'tw-flex tw-items-center tw-justify-center',
          '!tw-mt-0 !tw-mb-[10px] !tw-mx-auto' // (!) is not removed as it will be overwritten w/ existing styles for StatusIndicator component index.scss
        )}
      >
        {taskCompleted && (
          <Icon
            className={clsx('tw-mr-[3px] tw-text-[14px]')}
            icon={'check_circle'}
            iconType={ICON_TYPES.rounded}
          />
        )}
      </StatusIndicator>
    </TileCard>
  );
};

export default SubtaskReportItem;
